import { useEffect, useState } from "react";
import { ArrowUpRight, BookOpen, CalendarCheck2, CalendarClock, CalendarDays, CalendarOff, ContactRound, Home, Inbox, LayoutGrid, LifeBuoy, ListChecks, Lock, MessageSquareQuote, MessagesSquare, Pencil, Search, Settings, UsersRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { getAuthToken, subscribeAuthStateChange } from "@/lib/api/token-store";
import { usePermissions } from "@/hooks/use-permissions";
import { hasPermission, isMenuItemHidden, refreshPermissions } from "@/lib/stores/permissions-store";
import { getSidebarZoneColors } from "@/lib/sidebar/sidebar-icon-colors";
import { resolveVisibleZones } from "@/lib/sidebar/nav-zones";
import { getTaskCounts, subscribeTaskCounts } from "@/lib/stores/task-counts-store";
import { getThreadsNeedsCount, subscribeThreadsNeedsCount } from "@/lib/stores/threads-overview-store";
import {
  getChatsUnreadThreadsCount,
  getPrivateUnreadThreadsCount,
  subscribeChatsUnread,
  subscribePrivateUnread,
} from "@/lib/stores/chats-unread-store";
import { getDashboardUnseenCount, subscribeDashboardUnseenCount } from "@/lib/stores/dashboard-posts-store";
import { getNotesOpenTodoCount, subscribeNotesOpenTodoCount } from "@/lib/stores/user-notes-store";
import { getPendingMCCount, subscribePendingMCCount } from "@/lib/stores/work-plan-mc-store";
import ProfileSwitcher from "@/components/auth/profile-switcher";
import HealoLogo from "@/components/healo-logo";
import EnableNotificationsPrompt from "@/components/layout/enable-notifications-prompt";
import UpdateReadyBanner from "@/components/layout/update-ready-banner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isWebMode } from "@/lib/config/web-mode";

// The nav is cut into five ZONES and colour is pinned per zone, not per item — colour answers
// "which area of the app is this?", so the items read as five chunks instead of fourteen
// unrelated hues (see sidebar-icon-colors.js). Membership is pinned by item, exactly like the
// old per-item colour index was: hiding a conditional item like "Chats" must never re-tint the
// items below it. The five near-identical calendar glyphs share one amber zone on purpose — the
// zone chunks them, the glyph disambiguates inside it — and Tools is slate so it recedes.
//
// `module` maps the item onto the RBAC function matrix (src/lib/permissions.js): the item is
// HIDDEN unless the user holds at least view on that module (admins hold everything).
// `menuKey` is the item's key in the per-group "Menu visibility" list (MENU_ITEMS) — a
// cosmetic trim on top of the module gate, so items sharing a module can be hidden
// individually. Dashboard and Settings stay module- and menuKey-free — the login landing
// page and the device-local preferences must remain reachable for every staff user. (The
// Settings SUB-PAGES are individually hideable via their own settings_* menu keys; see
// settings-sub-rail.jsx.)
const NAV_ZONES = [
  {
    id: "overview",
    label: "Overview",
    hue: "violet",
    items: [
      // The badge is the unseen-announcements count (posts created since this user's last
      // dashboard visit; server-side stamp, so it travels across devices). Red by explicit
      // product decision, and it clears only when the Dashboard is actually opened.
      { title: "Dashboard", icon: Home, badge: "dashboardUnseen" },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    hue: "sky",
    items: [
      // The badge is the "Needs you" pile — threads waiting on a reply plus unanswered bot replies
      // past the threshold (see threads-overview-store) — so the count is visible before opening
      // Messages. Always rose: unlike tasks there is no non-urgent state to show green.
      { title: "Messages", icon: MessagesSquare, requiresAuth: true, badge: "messagesNeeds", module: "messages", menuKey: "messages" },
      // Internal staff chats; the chats module composes the backend's userType eligibility with
      // the RBAC grant (server-computed on the auth user + permissions map). The badge is the
      // number of chats with unread messages (chats-unread-store), mirroring Messages.
      { title: "Chats", icon: UsersRound, requiresAuth: true, badge: "chatsUnread", module: "chats", menuKey: "chats" },
      // Private 1:1 contact chats, started from the phone's address book. Deliberately NOT gated on
      // a module: these are personal conversations and the server gates them on membership alone
      // (requireDirectChatUser), so gating the menu entry would hide someone's own messages.
      { title: "Private", icon: Lock, requiresAuth: true, badge: "privateUnread", menuKey: "private" },
    ],
  },
  {
    id: "patients",
    label: "Patients",
    hue: "emerald",
    items: [
      // Patient directory: search/browse, add, and edit patients (same editor used in Messages).
      { title: "Patients", icon: ContactRound, requiresAuth: true, module: "patients", menuKey: "patients" },
      // Team task queue; the badge is the caller's open task count — green when none are overdue,
      // red when at least one is (see task-counts-store).
      { title: "Tasks", icon: ListChecks, requiresAuth: true, badge: "myTasks", module: "tasks", menuKey: "tasks" },
      // TODO: Re enable when the inbox is implemented
      // { title: "Inbox", icon: Inbox, requiresAuth: true },
    ],
  },
  {
    id: "scheduling",
    label: "Scheduling",
    hue: "amber",
    items: [
      { title: "Calendar", icon: CalendarDays, requiresAuth: true, module: "calendar", menuKey: "calendar" },
      { title: "Slots", icon: LayoutGrid, requiresAuth: true, module: "scheduling", menuKey: "slots" },
      // Working-sessions roster: who is where, which block (AM/PM), per-day overrides.
      { title: "Sessions", icon: CalendarClock, requiresAuth: true, module: "scheduling", menuKey: "sessions" },
      // Day work-plan planner (support-staff AM/PM roster + MC review). The badge is the
      // org-wide pending-MC count — a red "confirm me" pile, like Messages' needs-you badge.
      // Its own module: work_plan:view opens the page and the MC queue, work_plan:manage publishes
      // a day and decides MCs — while the posting edits inside the planner still need
      // scheduling:manage, so a roster editor and a publisher can be different people.
      { title: "Work Plan", icon: CalendarCheck2, requiresAuth: true, badge: "workPlanMc", module: "work_plan", menuKey: "work_plan" },
      { title: "Time Off", icon: CalendarOff, requiresAuth: true, module: "scheduling", menuKey: "time_off" },
    ],
  },
  {
    id: "tools",
    label: "Tools",
    hue: "slate",
    items: [
      // The Search page is purely archived-message search, so it belongs to the archive module.
      { title: "Search", icon: Search, requiresAuth: true, module: "archive", menuKey: "search" },
      // Opens the separate My Notes scratchpad WINDOW (the ↗ marks it as a window, not a
      // page): `action` items bypass onSelect entirely — setActiveWorkspacePage hard-rejects
      // unknown page names. Strictly personal, so no RBAC module; desktopOnly because the
      // window is Electron-only (web mode has nothing to open).
      { title: "My Notes", icon: Pencil, requiresAuth: true, badge: "notesTodos", trailingIcon: ArrowUpRight, action: "open-notes", desktopOnly: true },
      { title: "Knowledge Base", icon: BookOpen, requiresAuth: true, module: "knowledge", menuKey: "knowledge_base" },
      { title: "Trigger Base", icon: MessageSquareQuote, requiresAuth: true, module: "knowledge", menuKey: "trigger_base" },
      // { title: "Support", icon: LifeBuoy, requiresAuth: true },
      { title: "Settings", icon: Settings, requiresAuth: true },
    ],
  },
];

const NAV_ITEMS = NAV_ZONES.flatMap((zone) => zone.items);

// PAGE_MODULES lets App.jsx apply the same gate when rendering a page directly (deep links,
// notification jumps, revoked-while-open): page title → required module (view level).
export const PAGE_MODULES = Object.freeze(
  Object.fromEntries(
    NAV_ITEMS.filter((item) => item.module).map((item) => [item.title, item.module]),
  ),
);

// Zone micro-label for the expanded panel: a 7px dot in the zone's 500 hue plus the zone name.
// Replaces the old NAVIGATION / TOOLS section headers — five quiet labels instead of two loud ones.
function ZoneHeader({ zone }) {
  const tones = getSidebarZoneColors(zone.hue);
  return (
    <div className="flex items-center gap-1.5 px-2.5 pb-[5px] pt-2">
      <span className={cn("size-[7px] shrink-0 rounded-full", tones.dot)} aria-hidden="true" />
      <span className={cn("truncate text-[9.5px] font-semibold uppercase tracking-[0.1em]", tones.label)}>
        {zone.label}
      </span>
    </div>
  );
}

// A single nav row, shared by every zone and by both widths. At rest it is a bare zone-hued
// glyph — no tile, so neither the panel nor the rail turns into a wall of colour. The SELECTED
// item is what earns a tile: a translucent zone fill plus a hairline inset ring, and on the rail
// a soft same-hue glow as well. `badgeCount` (when > 0) renders as a trailing pill when expanded,
// where it can sit beside the label instead of covering the glyph, and as the small overlapping
// pip on the rail, which has no room for a pill.
function NavButton({
  item,
  zone,
  isActive,
  isDisabled,
  collapsed,
  badgeCount = 0,
  badgeTone = "danger",
  badgeTitle,
  onSelect,
  onAction,
}) {
  const tones = getSidebarZoneColors(zone);
  const badgeLabel = badgeTitle || String(badgeCount);
  const badgeText = badgeCount > 99 ? "99+" : badgeCount;
  const badgeFill =
    badgeTone === "success" ? "bg-emerald-600" : badgeTone === "primary" ? "bg-primary" : "bg-rose-600";
  const icon = (
    // The pip stays anchored to the ICON in both states. (The mock hangs it off the active tile
    // instead, which makes the count hop outward the moment the row is selected.) `ring-background`
    // punches it clear of the tile fill underneath.
    <span
      className={cn(
        "relative inline-flex shrink-0",
        isActive ? tones.glyphActive : collapsed ? tones.glyphRail : tones.glyph,
      )}
    >
      <item.icon className={cn("shrink-0", collapsed ? "size-[18px]" : "size-4")} strokeWidth={1.9} />
      {collapsed && badgeCount > 0 && (
        <span
          className={cn(
            "absolute -right-2 -top-[5px] inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-[3px] text-[9px] font-bold leading-none text-white ring-2 ring-background",
            badgeFill,
          )}
          title={badgeLabel}
          aria-label={badgeLabel}
        >
          {badgeText}
        </span>
      )}
    </span>
  );
  return (
    <button
      type="button"
      disabled={isDisabled}
      className={cn(
        "flex w-full items-center text-left transition-colors duration-150 ease-out",
        collapsed
          ? "h-[42px] justify-center"
          : cn("h-9 gap-2.5 rounded-sm px-2.5 text-[13px]", isActive ? tones.rowActive : !isDisabled && tones.hover),
        // Rest glyphs already run at zone-300 @ 80%, so the row's own opacity-50 lands a
        // logged-out glyph on the spec'd 40% without a second disabled-only colour.
        isDisabled && "cursor-not-allowed opacity-50",
      )}
      onClick={() => {
        if (isDisabled) return;
        // Action items (e.g. My Notes) trigger a side effect — never page navigation, which
        // would bounce off the workspace store's known-page check.
        if (item.action) {
          onAction?.(item.action);
          return;
        }
        onSelect?.(item.title);
      }}
      aria-current={isActive ? "page" : undefined}
    >
      {collapsed ? (
        // On the rail this 36px box IS the active tile; at rest it is invisible and carries only
        // the hover tint, so the hover target lines up exactly with the selected tile.
        <span
          className={cn(
            "flex size-9 items-center justify-center rounded-lg transition-colors duration-150 ease-out",
            isActive ? tones.tileActive : !isDisabled && tones.hover,
          )}
        >
          {icon}
        </span>
      ) : (
        icon
      )}
      {!collapsed && (
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            isActive ? "font-semibold text-foreground" : "text-foreground/85",
          )}
        >
          {item.title}
        </span>
      )}
      {/* ↗-style marker on action items: "opens a separate window, not a page". */}
      {!collapsed && item.trailingIcon && (
        <item.trailingIcon className="size-3.5 shrink-0 text-muted-foreground/70" aria-hidden="true" />
      )}
      {!collapsed && badgeCount > 0 && (
        <span
          className={cn(
            "inline-flex h-[17px] min-w-[21px] shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none text-white",
            badgeFill,
          )}
          title={badgeLabel}
          aria-label={badgeLabel}
        >
          {badgeText}
        </span>
      )}
    </button>
  );
}

function AppSidebar({ collapsed = false, activePage = "Dashboard", onPageSelect }) {
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(getAuthToken()));
  // Live RBAC state: which nav modules the user may see. Re-renders on login/refresh.
  usePermissions();
  // Tasks nav badge, fed by the shared counts store: the count is the caller's open tasks
  // (assigned to me), and the colour flips to red when any of them are overdue.
  const [myTaskCount, setMyTaskCount] = useState(() => Number(getTaskCounts()?.mine) || 0);
  const [myOverdueCount, setMyOverdueCount] = useState(() => Number(getTaskCounts()?.myOverdue) || 0);
  useEffect(
    () =>
      subscribeTaskCounts((counts) => {
        setMyTaskCount(Number(counts?.mine) || 0);
        setMyOverdueCount(Number(counts?.myOverdue) || 0);
      }),
    [],
  );
  // Messages nav badge, fed by the shared overview store: the "Needs you" count, started here so it
  // is fetched as soon as the app shell mounts (before the user opens Messages).
  const [messagesNeedsCount, setMessagesNeedsCount] = useState(() => Number(getThreadsNeedsCount()) || 0);
  useEffect(() => subscribeThreadsNeedsCount((count) => setMessagesNeedsCount(Number(count) || 0)), []);
  // Chats nav badge: how many internal chats hold unread messages, started here for the
  // same reason (visible before the user ever opens Chats).
  const [chatsUnreadCount, setChatsUnreadCount] = useState(() => Number(getChatsUnreadThreadsCount()) || 0);
  useEffect(() => subscribeChatsUnread((count) => setChatsUnreadCount(Number(count) || 0)), []);
  // Private nav badge: contact chats holding unread messages, on its own count so a private
  // message never inflates (or hides behind) the staff Chats bubble.
  const [privateUnreadCount, setPrivateUnreadCount] = useState(
    () => Number(getPrivateUnreadThreadsCount()) || 0,
  );
  useEffect(
    () => subscribePrivateUnread((count) => setPrivateUnreadCount(Number(count) || 0)),
    [],
  );
  // Work Plan nav badge: pending MC (sick leave) requests awaiting a manager's decision.
  const [pendingMcCount, setPendingMcCount] = useState(() => Number(getPendingMCCount()) || 0);
  useEffect(() => subscribePendingMCCount((count) => setPendingMcCount(Number(count) || 0)), []);
  // Dashboard nav badge: announcements the user has not seen yet (posts newer than their
  // last dashboard visit). Started here so new posts light up mid-session wherever the
  // user is (WS ping + interval keep the store fresh).
  const [dashboardUnseenCount, setDashboardUnseenCount] = useState(
    () => Number(getDashboardUnseenCount()) || 0,
  );
  useEffect(
    () => subscribeDashboardUnseenCount((count) => setDashboardUnseenCount(Number(count) || 0)),
    [],
  );
  // My Notes nav badge: open (unchecked) to-dos in the user's private scratchpad. Started
  // here so the count is warm before the notes window ever opens.
  const [notesTodoCount, setNotesTodoCount] = useState(() => Number(getNotesOpenTodoCount()) || 0);
  useEffect(() => subscribeNotesOpenTodoCount((count) => setNotesTodoCount(Number(count) || 0)), []);

  // Resolve a nav item's badge from the live counts. Returns null when there is nothing to show.
  const resolveNavBadge = (item) => {
    if (!isLoggedIn) return null;
    if (item.badge === "myTasks") {
      if (myTaskCount <= 0) return null;
      return {
        count: myTaskCount,
        tone: myOverdueCount > 0 ? "danger" : "success",
        title:
          myOverdueCount > 0
            ? `${myTaskCount} task${myTaskCount === 1 ? "" : "s"} assigned to you · ${myOverdueCount} overdue`
            : `${myTaskCount} task${myTaskCount === 1 ? "" : "s"} assigned to you`,
      };
    }
    if (item.badge === "messagesNeeds") {
      if (messagesNeedsCount <= 0) return null;
      return {
        count: messagesNeedsCount,
        tone: "danger",
        title: `${messagesNeedsCount} message${messagesNeedsCount === 1 ? "" : "s"} need${
          messagesNeedsCount === 1 ? "s" : ""
        } your attention`,
      };
    }
    if (item.badge === "chatsUnread") {
      if (chatsUnreadCount <= 0) return null;
      return {
        count: chatsUnreadCount,
        tone: "danger",
        title: `${chatsUnreadCount} chat${chatsUnreadCount === 1 ? "" : "s"} with unread messages`,
      };
    }
    if (item.badge === "privateUnread") {
      if (privateUnreadCount <= 0) return null;
      return {
        count: privateUnreadCount,
        tone: "danger",
        title: `${privateUnreadCount} private chat${privateUnreadCount === 1 ? "" : "s"} with unread messages`,
      };
    }
    if (item.badge === "workPlanMc") {
      if (pendingMcCount <= 0) return null;
      return {
        count: pendingMcCount,
        tone: "danger",
        title: `${pendingMcCount} medical certificate${pendingMcCount === 1 ? "" : "s"} awaiting confirmation`,
      };
    }
    if (item.badge === "dashboardUnseen") {
      if (dashboardUnseenCount <= 0) return null;
      return {
        count: dashboardUnseenCount,
        tone: "danger",
        title: `${dashboardUnseenCount} new announcement${dashboardUnseenCount === 1 ? "" : "s"}`,
      };
    }
    if (item.badge === "notesTodos") {
      if (notesTodoCount <= 0) return null;
      return {
        count: notesTodoCount,
        // Primary, not rose/emerald: open to-dos are the user's own jottings, not an alert.
        tone: "primary",
        title: `${notesTodoCount} open to-do${notesTodoCount === 1 ? "" : "s"} in My Notes`,
      };
    }
    return null;
  };

  // Side-effect nav items (My Notes opens a separate Electron window, not a pane).
  const handleNavAction = (action) => {
    if (action === "open-notes") {
      void window.electronAPI?.notesOpenWindow?.();
    }
  };

  useEffect(() => {
    const refresh = () => {
      const loggedIn = Boolean(getAuthToken());
      setIsLoggedIn(loggedIn);
      if (!loggedIn) {
        return;
      }
      // The permissions store seeds itself from the cached login payload; confirm against
      // the live backend so a stale session (e.g. logged in before a permission edit)
      // reflects current rights without a manual re-login.
      refreshPermissions();
    };
    refresh();
    return subscribeAuthStateChange(refresh);
  }, []);

  // RBAC + per-group menu visibility, resolved BEFORE render so a zone whose items are all
  // hidden drops its header too — no orphan "SCHEDULING" label sitting over nothing. Recomputes
  // on every permission change because usePermissions() above re-renders on one.
  const visibleZones = resolveVisibleZones(NAV_ZONES, {
    isLoggedIn,
    canView: (module) => hasPermission(module, "view"),
    isHidden: isMenuItemHidden,
    isDesktopApp: !isWebMode(),
  });

  // Width (incl. the collapsed rail snap) is owned by the resizable shell panel in App.jsx;
  // the resize handle doubles as the right border, so no border-r here.
  const containerClassName = "h-full w-full bg-muted/20";

  const messageThreads = [
    {
      id: "t-1",
      name: "Product Team",
      preview: "Can we ship the sidebar polish today?",
      time: "2m ago",
      unread: 3,
    },
    {
      id: "t-2",
      name: "Sarah Connor",
      preview: "I added feedback on the latest mockup.",
      time: "12m ago",
      unread: 0,
    },
    {
      id: "t-3",
      name: "Support Queue",
      preview: "Two new tickets need triage.",
      time: "25m ago",
      unread: 2,
    },
    {
      id: "t-4",
      name: "Design Review",
      preview: "Let us align on spacing and hierarchy.",
      time: "1h ago",
      unread: 0,
    },
    {
      id: "t-5",
      name: "Engineering",
      preview: "Build is green after dependency update.",
      time: "2h ago",
      unread: 1,
    },
  ];

  return (
    <aside className={containerClassName}>
      <div className="flex h-full flex-col">
        {/* Web mode: the brand banner lives here instead of the titlebar and collapses with
            the rail — icon only when collapsed, icon + name + version when expanded. */}
        {isWebMode() && (
          <div
            className={cn(
              "flex items-center gap-2.5 border-b border-border p-3",
              collapsed && "justify-center p-2",
            )}
          >
            <HealoLogo width={28} height={28} className="size-7 shrink-0 rounded-lg" />
            {!collapsed && (
              <div className="min-w-0 leading-tight">
                <p className="truncate text-sm font-semibold text-foreground">Clarimed</p>
                <p className="truncate text-[10px] text-muted-foreground">
                  v{import.meta.env.VITE_APP_VERSION}
                </p>
              </div>
            )}
          </div>
        )}
        {/* FIXME: it may be needed later */}
        {/* <div className="border-b border-border p-3">
          <input
            type="text"
            placeholder={collapsed ? '...' : 'Search...'}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div> */}

        <ScrollArea className="flex-1 min-h-0 p-2">
          {visibleZones.map(({ zone, items }, zoneIndex) => (
            <div
              key={zone.id}
              role="group"
              aria-label={zone.label}
              // Expanded, the zone header's own top padding is the separator. Collapsed, the
              // headers are gone, so a 10px gap does the chunking instead — indexed off the
              // VISIBLE zones so a fully hidden first zone doesn't leave the rail top-padded.
              className={collapsed && zoneIndex > 0 ? "mt-2.5" : undefined}
            >
              {!collapsed && <ZoneHeader zone={zone} />}
              {items.map((item) => {
                const badge = resolveNavBadge(item);
                return (
                  <NavButton
                    key={item.title}
                    item={item}
                    zone={zone.hue}
                    isActive={activePage === item.title}
                    isDisabled={Boolean(item.requiresAuth && !isLoggedIn)}
                    collapsed={collapsed}
                    badgeCount={badge?.count ?? 0}
                    badgeTone={badge?.tone ?? "danger"}
                    badgeTitle={badge?.title}
                    onSelect={onPageSelect}
                    onAction={handleNavAction}
                  />
                );
              })}
            </div>
          ))}

          {/* {!collapsed && (
            <section className="m-2 mt-4">
              <ScrollArea className="h-[200px] w-full rounded-md border p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Message Threads</p>
                <div className="mt-3 space-y-2">
                  {messageThreads.map((thread) => (
                    <article key={thread.id} className="w-[92%] rounded-md border border-border/60 bg-background px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-sm font-medium leading-none">{thread.name}</p>
                        <p className="shrink-0 text-[10px] text-muted-foreground">{thread.time}</p>
                      </div>
                      <p className="mt-1 truncate text-xs text-muted-foreground">{thread.preview}</p>
                      {thread.unread > 0 && <span className="mt-2 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">{thread.unread} new</span>}
                    </article>
                  ))}
                </div>
              </ScrollArea>
            </section>
          )} */}
        </ScrollArea>

        {/* Sidebar footer: update prompt (when ready; desktop) / enable-notifications prompt (web,
            until the browser permission is decided) + current-user chip / profile switcher.
            Navigation after a switch is event-driven — the workspace auth bridge activates the
            target user's workspace on their last page (or Dashboard for new profiles). */}
        {isLoggedIn ? (
          <div className="space-y-2 border-t border-border p-2">
            <UpdateReadyBanner variant="sidebar" collapsed={collapsed} />
            {isWebMode() ? <EnableNotificationsPrompt collapsed={collapsed} /> : null}
            <ProfileSwitcher collapsed={collapsed} />
          </div>
        ) : null}

        {/* <div className="border-t border-border p-3">
          <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            {collapsed ? 'Healo' : 'Healo layout demo'}
          </div>
        </div> */}
      </div>
    </aside>
  );
}

export default AppSidebar;
